import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { Button } from '@components/button';
import { RelativeSpinner, Spinner } from '@components/loading-spinner';
import { Stack } from '@components/stack';
import { Text } from '@components/text';
import { useToaster } from '@components/toaster';

import { ModalProps } from 'features/modals';
import { useStore } from 'models/Provider';

import { adManagerCreateApplication, adManagerGetJob, CreateApplicationErrorResponse } from './api';

type Props = ModalProps<{
  applicationID: string;
  jobID: string;
}>;

export const ADManagerCreateUserModal = (props: Props) => {
  const { t } = useStore();
  const { closeModal, applicationID, jobID } = props;
  const { showSuccess, showError } = useToaster();

  const jobQuery = useQuery({
    queryKey: ['ad-manager-job', jobID],
    queryFn: () => adManagerGetJob(jobID),
  });
  const client = useQueryClient();
  const createUser = useMutation<{}, CreateApplicationErrorResponse>(
    () => adManagerCreateApplication(applicationID),
    {
      onSuccess: () => {
        client.invalidateQueries(['ad-manager-application', applicationID]);
        showSuccess(t('integrations.admanager.application.create.success'));
        closeModal();
      },
      onError: (error) => {
        showError(
          error.response?.data.message ?? t('integrations.admanager.application.create.error')
        );
      },
    }
  );

  if (jobQuery.isLoading) {
    return <RelativeSpinner />;
  }

  return (
    <Stack space={6}>
      {createUser.isLoading && <Spinner />}
      <Text font="heading1" text={t('integrations.admanager.modal.header')} />
      <Stack space={0}>
        <Text font="semibold14" text={t('integrations.admanager.modal.template.label')} />
        <Text text={jobQuery.data?.templateName} />
      </Stack>
      <Stack space={4} orientation="horizontal" justify={'flex-end'}>
        <Button
          variant="secondary"
          text={t('common.forms.button.cancel')}
          onClick={closeModal}
          isDisabled={createUser.isLoading}
        />
        <Button
          text={t('integrations.admanager.modal.button.submit')}
          onClick={() => createUser.mutate()}
          isDisabled={createUser.isLoading}
        />
      </Stack>
    </Stack>
  );
};
